import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const COLORS = ["#3A1733", "#7A1C2A", "#C6A87C", "#E8D8C1", "#1C1C1C"];

export function ReportsAdmin() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-charcoal/40">Loading reports…</div>;

  const totalRev = data.monthly.reduce((s, m) => s + (m.revenue || 0), 0);
  const totalMembers = data.monthly.reduce((s, m) => s + (m.members || 0), 0);
  const paidMembers = data.monthly.reduce((s, m) => s + (m.paid || 0), 0);

  return (
    <div>
      <div className="brand-eyebrow">Reports</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Analytics</h1>
      <p className="brand-body mt-3 text-charcoal/60 max-w-2xl">A twelve-month view of revenue, member growth, and content mix.</p>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <StatCard label="Revenue (12 mo)" value={`$${totalRev.toFixed(2)}`} />
        <StatCard label="New members (12 mo)" value={totalMembers} />
        <StatCard label="Paid conversions (12 mo)" value={paidMembers} />
      </div>

      <div className="mt-8 editorial-card p-6">
        <div className="brand-eyebrow mb-2">Revenue over time</div>
        <h3 className="font-serif text-2xl">Monthly revenue</h3>
        <div className="h-72 mt-6" data-testid="reports-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8D8C1" />
              <XAxis dataKey="label" stroke="#595959" fontSize={11} />
              <YAxis stroke="#595959" fontSize={11} />
              <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #C6A87C", borderRadius: 8 }} />
              <Line type="monotone" dataKey="revenue" stroke="#3A1733" strokeWidth={2.5} dot={{ r: 3, fill: "#C6A87C" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="editorial-card p-6">
          <div className="brand-eyebrow mb-2">Membership</div>
          <h3 className="font-serif text-2xl">Signups & paid conversions</h3>
          <div className="h-64 mt-6" data-testid="reports-members-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8D8C1" />
                <XAxis dataKey="label" stroke="#595959" fontSize={11} />
                <YAxis stroke="#595959" fontSize={11} />
                <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #C6A87C", borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="members" fill="#C6A87C" name="New members" radius={[4, 4, 0, 0]} />
                <Bar dataKey="paid" fill="#3A1733" name="Paid" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="editorial-card p-6">
          <div className="brand-eyebrow mb-2">Content Mix</div>
          <h3 className="font-serif text-2xl">Video access levels</h3>
          <div className="h-64 mt-6" data-testid="reports-content-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.access_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {data.access_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #C6A87C", borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 editorial-card p-6">
        <div className="brand-eyebrow mb-2">Editorial</div>
        <h3 className="font-serif text-2xl">Recent published posts</h3>
        <div className="mt-4 divide-y divide-gold-light">
          {data.top_posts.map((p) => (
            <div key={p.id} className="py-3 flex justify-between items-baseline" data-testid={`reports-top-${p.id}`}>
              <div className="font-serif text-lg">{p.title}</div>
              <div className="brand-eyebrow">{p.category}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="editorial-card p-5">
      <div className="brand-eyebrow">{label}</div>
      <div className="font-serif text-3xl text-plum mt-2">{value}</div>
    </div>
  );
}
